/**
 * Wraps a dynamic import() with retry logic and auto-reload for chunk load errors.
 *
 * When a deployment happens while users have the app open, old chunk files
 * may no longer exist on the server. This helper:
 * 1. Retries the import once after a short delay (for transient network errors)
 * 2. If the retry fails, automatically reloads the page once
 * 3. Uses sessionStorage to prevent infinite reload loops
 * 4. Uses a global flag to prevent parallel chunk failures from causing duplicate retries
 */

const MAX_RETRIES = 1;
const RETRY_DELAY_MS = 500;
const SESSION_STORAGE_PREFIX = "chunk-reload-";
// Flags älter als 30 Sekunden gelten als Altlasten eines früheren Deployments.
// Jüngere Flags bleiben als Infinite-Loop-Schutz erhalten.
const RELOAD_FLAG_MAX_AGE_MS = 30000;

// Globales Flag: Sobald ein Chunk-Fehler den Reload auslöst,
// brechen alle weiteren parallelen Chunk-Fehler sofort ab.
let reloadTriggered = false;

/**
 * Prüft ob ein Fehler ein Chunk-Load-Fehler ist (fehlende/veraltete Datei nach Deployment)
 */
const isChunkLoadError = (error: Error): boolean => {
    return (
        error.name === "ChunkLoadError" ||
        (error.message != null && error.message.indexOf("Loading chunk") !== -1) ||
        (error.message != null && error.message.indexOf("Loading CSS chunk") !== -1)
    );
};

/**
 * Wartet die angegebene Anzahl Millisekunden
 */
const wait = (ms: number): Promise<void> =>
    new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Versucht einen dynamischen Import mit Retry-Logik und automatischem Seiten-Reload.
 *
 * Ablauf:
 * 1. Import wird einmal erneut versucht nach kurzer Pause (transiente Netzwerkfehler)
 * 2. Wenn der Retry fehlschlägt und es ein Chunk-Load-Fehler ist:
 *    - Prüfe ob bereits ein anderer Chunk-Fehler den Reload ausgelöst hat (global)
 *    - Prüfe ob für diese Seite bereits ein Reload durchgeführt wurde (sessionStorage)
 *    - Falls nein: Speichere Flag und lade die Seite neu
 *    - Falls ja: Entferne Flag und werfe den Fehler (Fallback-UI wird angezeigt)
 * 3. Nicht-Chunk-Fehler werden sofort durchgeworfen
 */
const retryChunkLoad = <T>(
    importFn: () => Promise<T>
): Promise<T> => {
    return new Promise<T>((resolve, reject) => {
        const attempt = (retriesLeft: number): void => {
            // Ein anderer Chunk hat bereits den Reload ausgelöst → abbrechen
            if (reloadTriggered) {
                return;
            }

            importFn()
                .then(resolve)
                .catch((error: Error) => {
                    // Kein Chunk-Load-Fehler → sofort durchwerfen
                    if (!isChunkLoadError(error)) {
                        // Absichtlich console.error statt logger: Modul-Init schlägt fehl
                        // BEVOR der App-Logger-Client (Auth/Tenant-Kontext) verfügbar ist.
                        // eslint-disable-next-line no-console
                        console.error("[retryChunkLoad] module init failed", error);
                        reject(error);
                        return;
                    }

                    // Ein anderer Chunk hat zwischenzeitlich den Reload ausgelöst
                    if (reloadTriggered) {
                        return;
                    }

                    // Noch Retries übrig → erneut versuchen
                    if (retriesLeft > 0) {
                        wait(RETRY_DELAY_MS).then(() => attempt(retriesLeft - 1));
                        return;
                    }

                    // Alle Retries aufgebraucht → einmalig Seite neu laden
                    const pageKey =
                        SESSION_STORAGE_PREFIX + window.location.pathname;

                    try {
                        const reloadedAt = sessionStorage.getItem(pageKey);
                        const hasReloaded =
                            reloadedAt != null &&
                            Date.now() - Number(reloadedAt) <
                                RELOAD_FLAG_MAX_AGE_MS;

                        if (!hasReloaded) {
                            reloadTriggered = true;
                            sessionStorage.setItem(
                                pageKey,
                                String(Date.now())
                            );
                            window.location.reload();
                            return;
                        }

                        // Bereits reloaded → Flag entfernen und Fehler durchwerfen
                        sessionStorage.removeItem(pageKey);
                    } catch (_storageError) {
                        // sessionStorage nicht verfügbar (z.B. Private Browsing)
                        // → Fehler durchwerfen, Fallback-UI wird angezeigt
                    }

                    reject(error);
                });
        };

        attempt(MAX_RETRIES);
    });
};

export {retryChunkLoad, isChunkLoadError};

// Beim App-Start: veraltete chunk-reload-Flags aus sessionStorage entfernen.
// Flags älter als RELOAD_FLAG_MAX_AGE_MS stammen von einem früheren Deployment
// und würden sonst den automatischen Reload blockieren.
// Jüngere Flags bleiben erhalten, um Endlos-Reload-Loops bei defekten
// Deployments zu verhindern.
try {
    const now = Date.now();
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key != null && key.indexOf(SESSION_STORAGE_PREFIX) === 0) {
            const timestamp = Number(sessionStorage.getItem(key));
            if (isNaN(timestamp) || now - timestamp >= RELOAD_FLAG_MAX_AGE_MS) {
                keysToRemove.push(key);
            }
        }
    }
    keysToRemove.forEach((key) => sessionStorage.removeItem(key));
} catch (_e) {
    // sessionStorage nicht verfügbar
}
